<template>
  <VCard class="add-processing wishlist-create pa-0">
    <SLoadingOverlay :loading="loading" />
    <VCardTitle
      class="text-uppercase font-weight-bold pa-4"
      :class="{ 'py-3': isAttached }"
    >
      <VRow no-gutters>
        <VCol cols="11">
          <h4>
            {{ $t("wishlist.popup.header") }}
          </h4>
        </VCol>
        <VCol cols="1">
          <SBtn
            color="primary"
            class="mr-n3"
            text
            data-testid="close"
            @click.stop.prevent="$emit('close')"
          >
            <SIcon :icon="icons.mdiClose" :label="$t('dialog.close.aria-label')" />
          </SBtn>
        </VCol>
      </VRow>
    </VCardTitle>
    <VDivider class="mx-4" />
    <VCardText
      v-for="list in allLists"
      :key="list.id"
      class="pb-0"
      :class="isAttached ? 'pt-5' : 'pt-6'"
    >
      <SBtn
        elevation="0"
        class="text-underline list-button"
        text
        :class="{'text-secondary': listHasItem(list.id)}"
        data-focus-first
        data-testid="add-to-wishlist"
        @click.stop.prevent="toggleWishlistItem(list.id, list.name)"
      >
        <span class="text-truncate">
          {{ list.name }}
        </span>
        <VIcon v-if="listHasItem(list.id)" right small color="secondary">
          {{ icons.mdiCheckCircle }}
        </VIcon>
      </SBtn>
    </VCardText>
    <VCardActions class="background-secondary mt-6 pa-4">
      <SBtn
        elevation="0"
        text
        class="text-underline"
        data-focus-first
        data-testid="add-list"
        @click.stop.prevent="createList"
      >
        + {{ $t("wishlist.title.create") }}
      </SBtn>
    </VCardActions>
  </VCard>
</template>

<script lang="ts" setup>
import { mdiCheckCircle, mdiClose } from '@mdi/js'
import { storeToRefs } from 'pinia'
import type { ListedProduct } from '~/types/shop'
import { useWishlistStore } from '~/stores/wishlist'

interface Props {
 product: ListedProduct
 isAttached?: boolean
 loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isAttached: false,
  loading: false
})

const emits = defineEmits<{
  (e: 'create-list' | 'close'): void
  (e: 'updating', isUpdating: boolean): void
}>()

const icons = { mdiCheckCircle, mdiClose }

const createList = () => {
  emits('create-list')
  emits('close')
}

const $i18n = useI18n()

const wishlistStore = useWishlistStore()

const { allLists } = storeToRefs(wishlistStore)
const listHasItem = (wishlistId: string) => {
  return wishlistStore.listsWithItem.includes(wishlistId)
}

const notificationText = (listName: string) => {
  return $i18n.t('notifications.wishlist.text.added', { listName })
}
const toggleWishlistItem = async (wishlistId: string, wishlistName: string) => {
  emits('close')
  emits('updating', true)
  try {
    if (listHasItem(wishlistId)) {
      await wishlistStore.removeItemFromWishlist({
        wishlistId,
        itemId: props.product.id
      })
    } else {
      await wishlistStore.addItemToWishlist({
        wishlistId,
        itemId: props.product.id,
        quantity: 1,
        text: notificationText(wishlistName)
      })
    }
  } finally {
    emits('updating', false)
  }
}
</script>

<style lang="scss" scoped>
hr {
  margin: 0 0;
}

.list-button {
  height: auto;
  min-width: 0;
  max-width: 100%;
  padding: 0;
}
</style>
